/**
 * Export utilities for evaluation reports.
 * Supports: plain text, markdown, HTML.
 */

import type { EvaluationReport } from '@/types/stock';
import { formatCurrency, formatPercent, formatNumber } from './analysisEngine';

function formatVal(value: number | null, type: 'currency' | 'percent' | 'number'): string {
  if (value === null) return 'N/A';
  if (type === 'currency') return formatCurrency(value);
  if (type === 'percent') return formatPercent(value);
  return formatNumber(value);
}

// ============================================================
// Plain Text Export
// ============================================================

export function exportAsText(report: EvaluationReport): string {
  const lines: string[] = [];
  const sep = '='.repeat(60);
  const dash = '-'.repeat(60);

  lines.push(sep);
  lines.push(`STOCK FUNDAMENTAL ANALYSIS: ${report.ticker}`);
  lines.push(sep);
  lines.push(`Company: ${report.companyName}`);
  lines.push(`Exchange: ${report.exchange}`);
  lines.push(`Evaluation Date: ${new Date(report.evaluationDate * 1000).toLocaleDateString()}`);
  lines.push(`Analysis Period: ${report.analysisPeriod.startYear}–${report.analysisPeriod.endYear}`);
  lines.push(`Overall Status: ${report.overallStatus} (Score: ${report.score}/100)`);
  lines.push(`Data Source: ${report.dataSource.replace('_', ' ').toUpperCase()}`);
  lines.push('');

  lines.push('SECTOR ANALYSIS');
  lines.push(dash);
  lines.push(`Sector: ${report.sectorAnalysis.gicsSector}`);
  lines.push(`Industry: ${report.sectorAnalysis.industry}`);
  lines.push(`Classification: ${report.sectorAnalysis.classification}`);
  lines.push(`Moat Indicators:`);
  report.sectorAnalysis.moatIndicators.forEach((m) => lines.push(`  • ${m}`));
  lines.push('');

  lines.push('EVALUATION CRITERIA');
  lines.push(dash);
  report.criteria.forEach((c) => {
    const status = c.passed === true ? '[PASS]' : c.passed === false ? '[FAIL]' : '[N/A ]';
    lines.push(`${status} ${c.label}`);
    lines.push(`       Value: ${c.value !== null ? c.value.toFixed(2) : 'N/A'} | Target: ${c.target}`);
  });
  lines.push('');

  lines.push('ANNUAL METRICS');
  lines.push(dash);

  // Header row
  const years = report.annualMetrics.map((m) => String(m.fiscalYear));
  lines.push(['Metric'.padEnd(24), ...years.map((y) => y.padStart(12))].join(''));
  lines.push('-'.repeat(24 + years.length * 13));

  const rows: Array<[string, Array<number | null>, 'currency' | 'percent' | 'number']> = [
    ['Revenue', report.annualMetrics.map((m) => m.revenue), 'currency'],
    ['Gross Profit', report.annualMetrics.map((m) => m.grossProfit), 'currency'],
    ['Net Income', report.annualMetrics.map((m) => m.netIncome), 'currency'],
    ['EPS', report.annualMetrics.map((m) => m.eps), 'number'],
    ['Gross Margin', report.annualMetrics.map((m) => m.grossMargin), 'percent'],
    ['Net Margin', report.annualMetrics.map((m) => m.profitMargin), 'percent'],
    ['Free Cash Flow', report.annualMetrics.map((m) => m.freeCashFlow), 'currency'],
    ['Current Ratio', report.annualMetrics.map((m) => m.currentRatio), 'number'],
  ];

  for (const [label, values, type] of rows) {
    const cells = values.map((v) => formatVal(v, type).padStart(12));
    lines.push([label.padEnd(24), ...cells].join(''));
  }
  lines.push('');

  if (report.comments) {
    lines.push('NOTES & COMMENTS');
    lines.push(dash);
    lines.push(report.comments);
    lines.push('');
  }

  lines.push(sep);
  lines.push(`Generated by Stock Fundamentals | ${new Date().toISOString()}`);

  return lines.join('\n');
}

// ============================================================
// Markdown Export
// ============================================================

export function exportAsMarkdown(report: EvaluationReport): string {
  const lines: string[] = [];

  lines.push(`# ${report.ticker} — Fundamental Analysis`);
  lines.push('');
  lines.push(`| | |`);
  lines.push(`|---|---|`);
  lines.push(`| **Company** | ${report.companyName} |`);
  lines.push(`| **Exchange** | ${report.exchange} |`);
  lines.push(`| **Evaluation Date** | ${new Date(report.evaluationDate * 1000).toLocaleDateString()} |`);
  lines.push(`| **Analysis Period** | ${report.analysisPeriod.startYear}–${report.analysisPeriod.endYear} |`);
  lines.push(`| **Status** | **${report.overallStatus}** (${report.score}/100) |`);
  lines.push(`| **Sector** | ${report.sectorAnalysis.gicsSector} — ${report.sectorAnalysis.classification} |`);
  lines.push('');

  lines.push(`## Evaluation Criteria`);
  lines.push('');
  lines.push(`| Criteria | Value | Target | Status |`);
  lines.push(`|---|---|---|---|`);
  report.criteria.forEach((c) => {
    const status = c.passed === true ? '✅ PASS' : c.passed === false ? '❌ FAIL' : '⚪ N/A';
    lines.push(`| ${c.label} | ${c.value !== null ? c.value.toFixed(2) : 'N/A'} | ${c.target} | ${status} |`);
  });
  lines.push('');

  lines.push(`## Annual Metrics`);
  lines.push('');

  const years = report.annualMetrics.map((m) => m.fiscalYear);
  const header = `| Metric | ${years.join(' | ')} |`;
  const divider = `|---|${years.map(() => '---').join('|')}|`;
  lines.push(header);
  lines.push(divider);

  const tableRows: Array<[string, Array<number | null>, 'currency' | 'percent' | 'number']> = [
    ['Revenue', report.annualMetrics.map((m) => m.revenue), 'currency'],
    ['Revenue Growth', report.annualMetrics.map((m) => m.revenueGrowth), 'percent'],
    ['Gross Profit', report.annualMetrics.map((m) => m.grossProfit), 'currency'],
    ['Gross Margin', report.annualMetrics.map((m) => m.grossMargin), 'percent'],
    ['Net Income', report.annualMetrics.map((m) => m.netIncome), 'currency'],
    ['Net Margin', report.annualMetrics.map((m) => m.profitMargin), 'percent'],
    ['EPS', report.annualMetrics.map((m) => m.eps), 'number'],
    ['EPS Growth', report.annualMetrics.map((m) => m.epsGrowth), 'percent'],
    ['Free Cash Flow', report.annualMetrics.map((m) => m.freeCashFlow), 'currency'],
    ['FCF Margin', report.annualMetrics.map((m) => m.fcfMargin), 'percent'],
    ['Current Ratio', report.annualMetrics.map((m) => m.currentRatio), 'number'],
    ['Debt/Equity', report.annualMetrics.map((m) => m.debtToEquity), 'number'],
  ];

  for (const [label, values, type] of tableRows) {
    const cells = values.map((v) => formatVal(v, type));
    lines.push(`| ${label} | ${cells.join(' | ')} |`);
  }
  lines.push('');

  lines.push(`## Sector & Competitive Analysis`);
  lines.push('');
  lines.push(`- **Sector:** ${report.sectorAnalysis.gicsSector} (GICS ${report.sectorAnalysis.gicsCode})`);
  lines.push(`- **Industry:** ${report.sectorAnalysis.industry}`);
  lines.push(`- **Classification:** ${report.sectorAnalysis.classification}`);
  lines.push('');
  lines.push('**Moat Indicators:**');
  report.sectorAnalysis.moatIndicators.forEach((m) => lines.push(`- ${m}`));
  lines.push('');

  if (report.comments) {
    lines.push(`## Notes & Comments`);
    lines.push('');
    lines.push(report.comments);
    lines.push('');
  }

  lines.push('---');
  lines.push(`*Generated by [Stock Fundamentals](https://shakespeare.diy) on ${new Date().toLocaleDateString()}*`);

  return lines.join('\n');
}

// ============================================================
// HTML Export (self-contained)
// ============================================================

export function exportAsHtml(report: EvaluationReport): string {
  const md = exportAsMarkdown(report);
  const rows = report.annualMetrics;
  const years = rows.map((m) => m.fiscalYear);

  const statusColor = {
    PASS: '#10b981',
    FAIL: '#ef4444',
    WATCH: '#f59e0b',
    PENDING: '#6b7280',
  }[report.overallStatus];

  const tableRows = (rows: Array<number | null>, label: string, type: 'currency' | 'percent' | 'number') => {
    const cells = rows.map((v) => `<td>${formatVal(v, type)}</td>`).join('');
    return `<tr><th>${label}</th>${cells}</tr>`;
  };

  const criteriaRows = report.criteria.map((c) => {
    const color = c.passed === true ? '#10b981' : c.passed === false ? '#ef4444' : '#6b7280';
    const icon = c.passed === true ? '✓' : c.passed === false ? '✗' : '?';
    return `<tr>
      <td>${c.label}</td>
      <td>${c.value !== null ? c.value.toFixed(2) : 'N/A'}</td>
      <td>${c.target}</td>
      <td style="color:${color};font-weight:bold;">${icon} ${c.passed === true ? 'PASS' : c.passed === false ? 'FAIL' : 'N/A'}</td>
    </tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${report.ticker} — Fundamental Analysis</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; max-width: 960px; margin: 0 auto; padding: 32px 16px; color: #1a1a2e; line-height: 1.6; }
    h1 { font-size: 1.8rem; margin-bottom: 4px; }
    h2 { font-size: 1.2rem; margin-top: 2rem; border-bottom: 2px solid #e5e7eb; padding-bottom: 6px; }
    .status { display: inline-block; padding: 4px 14px; border-radius: 999px; color: white; font-weight: bold; font-size: 1rem; background: ${statusColor}; margin: 8px 0; }
    .score { font-size: 1.1rem; color: #6b7280; margin-left: 12px; }
    table { width: 100%; border-collapse: collapse; margin: 1rem 0; font-size: 0.9rem; }
    th, td { padding: 8px 12px; border: 1px solid #e5e7eb; text-align: right; }
    th:first-child, td:first-child { text-align: left; background: #f9fafb; font-weight: 600; }
    thead th { background: #f3f4f6; font-weight: 700; text-align: center; }
    .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin: 1rem 0; padding: 16px; background: #f9fafb; border-radius: 8px; font-size: 0.9rem; }
    .meta-item { display: flex; gap: 8px; }
    .meta-label { color: #6b7280; font-weight: 500; min-width: 120px; }
    ul { padding-left: 1.5rem; }
    li { margin: 4px 0; }
    .comments { background: #f9fafb; padding: 16px; border-radius: 8px; border-left: 4px solid ${statusColor}; white-space: pre-wrap; }
    footer { margin-top: 3rem; text-align: center; color: #9ca3af; font-size: 0.8rem; border-top: 1px solid #e5e7eb; padding-top: 1rem; }
  </style>
</head>
<body>
  <h1>${report.ticker} — Fundamental Analysis</h1>
  <span class="status">${report.overallStatus}</span>
  <span class="score">Score: ${report.score}/100</span>

  <div class="meta">
    <div class="meta-item"><span class="meta-label">Company:</span><span>${report.companyName}</span></div>
    <div class="meta-item"><span class="meta-label">Exchange:</span><span>${report.exchange}</span></div>
    <div class="meta-item"><span class="meta-label">Evaluation Date:</span><span>${new Date(report.evaluationDate * 1000).toLocaleDateString()}</span></div>
    <div class="meta-item"><span class="meta-label">Analysis Period:</span><span>${report.analysisPeriod.startYear}–${report.analysisPeriod.endYear}</span></div>
    <div class="meta-item"><span class="meta-label">Sector:</span><span>${report.sectorAnalysis.gicsSector}</span></div>
    <div class="meta-item"><span class="meta-label">Classification:</span><span>${report.sectorAnalysis.classification}</span></div>
  </div>

  <h2>Evaluation Criteria</h2>
  <table>
    <thead><tr><th>Criteria</th><th>Value</th><th>Target</th><th>Status</th></tr></thead>
    <tbody>${criteriaRows}</tbody>
  </table>

  <h2>Annual Metrics</h2>
  <table>
    <thead><tr><th>Metric</th>${years.map((y) => `<th>${y}</th>`).join('')}</tr></thead>
    <tbody>
      ${tableRows(rows.map((m) => m.revenue), 'Revenue', 'currency')}
      ${tableRows(rows.map((m) => m.revenueGrowth), 'Revenue Growth', 'percent')}
      ${tableRows(rows.map((m) => m.grossProfit), 'Gross Profit', 'currency')}
      ${tableRows(rows.map((m) => m.grossMargin), 'Gross Margin', 'percent')}
      ${tableRows(rows.map((m) => m.netIncome), 'Net Income', 'currency')}
      ${tableRows(rows.map((m) => m.profitMargin), 'Net Margin', 'percent')}
      ${tableRows(rows.map((m) => m.eps), 'EPS', 'number')}
      ${tableRows(rows.map((m) => m.epsGrowth), 'EPS Growth', 'percent')}
      ${tableRows(rows.map((m) => m.freeCashFlow), 'Free Cash Flow', 'currency')}
      ${tableRows(rows.map((m) => m.fcfMargin), 'FCF Margin', 'percent')}
      ${tableRows(rows.map((m) => m.currentRatio), 'Current Ratio', 'number')}
      ${tableRows(rows.map((m) => m.debtToEquity), 'Debt/Equity', 'number')}
    </tbody>
  </table>

  <h2>Sector & Competitive Analysis</h2>
  <ul>
    ${report.sectorAnalysis.moatIndicators.map((m) => `<li>${m}</li>`).join('')}
  </ul>

  ${report.comments ? `<h2>Notes & Comments</h2><div class="comments">${report.comments}</div>` : ''}

  <footer>Generated by <a href="https://shakespeare.diy">Stock Fundamentals</a> · ${new Date().toLocaleDateString()}</footer>
</body>
</html>`;
}

export function downloadReport(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
